import { useState } from 'react';
import { PageHeader } from '@/components/brand/PageHeader';
import { Panel } from '@/components/brand/Panel';
import { useApiQuery } from '@/lib/hooks';
import { EmptyState, ErrorState, LoadingState, UnauthorizedState } from '@/components/brand/States';
import { useAuth } from '@/lib/auth';
import { SevPill } from '@/components/brand/SevPill';

interface ArchivedEvent {
  id: string;
  event_id: string;
  occurred_at: number;
  source: string;
  event_type: string;
  status: string;
  severity: string | null;
  user_email: string | null;
  duration_ms: number | null;
  cost_gbp: number | null;
  error_message: string | null;
}

function toUnixMs(yyyymmdd: string, endOfDay = false): number {
  const [y, m, d] = yyyymmdd.split('-').map(Number);
  if (!y || !m || !d) return 0;
  const date = new Date(Date.UTC(y, m - 1, d, endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, endOfDay ? 999 : 0));
  return date.getTime();
}

function defaultIso(daysAgo: number): string {
  const d = new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000);
  return d.toISOString().slice(0, 10);
}

export function ArchivePage() {
  const { me, isUnauthorized } = useAuth();
  const [from, setFrom] = useState(defaultIso(90));
  const [to, setTo] = useState(defaultIso(31));
  const [source, setSource] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [params, setParams] = useState<{ from: number; to: number; source: string; status: string; q: string } | null>(null);

  // All hooks must run on every render — keep the isUnauthorized /
  // non-admin bail-outs *below* useApiQuery, or the hook count changes
  // between renders and React throws "rendered fewer hooks than
  // expected" (which blanks the page). params starts null so the query
  // stays disabled for these users anyway.
  const search = useApiQuery<{ items: ArchivedEvent[]; scanned_shards: number; scanned_rows: number; truncated: boolean }>({
    enabled: params !== null,
    queryKey: ['archive', params],
    queryFn: async () => {
      const u = new URL('/api/archive/search', location.origin);
      if (params) {
        u.searchParams.set('from', String(params.from));
        u.searchParams.set('to', String(params.to));
        if (params.source) u.searchParams.set('source', params.source);
        if (params.status) u.searchParams.set('status', params.status);
        if (params.q) u.searchParams.set('q', params.q);
        u.searchParams.set('limit', '200');
      }
      const res = await fetch(u.toString(), { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
      return res.json();
    },
  });

  if (isUnauthorized) return <UnauthorizedState />;
  if (me && me.role !== 'admin') {
    return (
      <div>
        <PageHeader eyebrow="Phase 2" title="Archive" />
        <Panel title="Archive search"><EmptyState title="Admin-only" hint="Archive contains 31-day-old to 365-day-old events from the R2 cold store." /></Panel>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <PageHeader eyebrow="Phase 2" title="Archive" meta="R2 cold store · 31–365 days" />
      <Panel title="Search">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setParams({ from: toUnixMs(from), to: toUnixMs(to, true), source, status, q });
          }}
          className="grid grid-cols-1 md:grid-cols-3 gap-3 text-small"
        >
          <label className="flex flex-col gap-1">
            <span className="eyebrow">From (UTC)</span>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="border border-rule px-3 py-2 bg-bg-page mono" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="eyebrow">To (UTC)</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="border border-rule px-3 py-2 bg-bg-page mono" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="eyebrow">Source</span>
            <select value={source} onChange={(e) => setSource(e.target.value)} className="border border-rule px-3 py-2 bg-bg-page">
              <option value="">Any</option>
              <option value="hubspot">hubspot</option>
              <option value="cloudflare_worker">cloudflare_worker</option>
              <option value="sap_b1">sap_b1</option>
              <option value="m365">m365</option>
              <option value="anthropic">anthropic</option>
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="eyebrow">Status</span>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-rule px-3 py-2 bg-bg-page">
              <option value="">Any</option>
              <option value="success">success</option>
              <option value="failed">failed</option>
              <option value="timeout">timeout</option>
              <option value="warning">warning</option>
              <option value="info">info</option>
            </select>
          </label>
          <label className="flex flex-col gap-1 md:col-span-2">
            <span className="eyebrow">Contains text</span>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="event_id, error message, automation name…" className="border border-rule px-3 py-2 bg-bg-page" />
          </label>
          <div className="md:col-span-3 flex justify-end">
            <button type="submit" disabled={search.isFetching} className="bg-fg-default text-mb-spring-wood px-4 py-2 text-eyebrow disabled:opacity-40">
              {search.isFetching ? 'Searching…' : 'Search archive'}
            </button>
          </div>
        </form>
      </Panel>

      {params && (
        <Panel
          title="Results"
          subtitle={
            search.data
              ? `${search.data.items.length} rows · ${search.data.scanned_shards} shards, ${search.data.scanned_rows} rows scanned${search.data.truncated ? ' · truncated' : ''}`
              : undefined
          }
        >
          {search.isLoading && <LoadingState label="Streaming archive…" />}
          {search.error && <ErrorState title="Search failed" detail={String((search.error as Error).message)} />}
          {search.data && search.data.items.length === 0 && (
            <EmptyState
              title="No matching events"
              hint="The archive worker runs daily 02:00 UTC. Events become archive-able once they're 31+ days old."
            />
          )}
          {search.data && search.data.items.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-small">
                <thead className="text-fg-muted text-eyebrow">
                  <tr><th className="text-left py-2">When (UTC)</th><th className="text-left">Source</th><th className="text-left">Event type</th><th className="text-left">Status</th><th className="text-right">£</th><th className="text-right">ms</th></tr>
                </thead>
                <tbody className="divide-y divide-rule">
                  {search.data.items.map((e) => (
                    <tr key={e.id}>
                      <td className="py-2 mono mono-sm">{new Date(e.occurred_at).toISOString()}</td>
                      <td className="mono">{e.source}</td>
                      <td className="mono">{e.event_type}</td>
                      <td>
                        <SevPill
                          level={
                            e.status === 'failed' || e.status === 'timeout'
                              ? 'crit'
                              : e.status === 'warning'
                                ? 'warn'
                                : e.status === 'info'
                                  ? 'info'
                                  : 'ok'
                          }
                          label={e.status}
                        />
                      </td>
                      <td className="text-right num">{e.cost_gbp != null ? e.cost_gbp.toFixed(4) : '—'}</td>
                      <td className="text-right num">{e.duration_ms ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}
    </div>
  );
}
